import React, {useCallback, useEffect, useMemo, useRef, useState} from "react"
import Select, {components, InputActionMeta, MenuListProps, NoticeProps} from "react-select"
import debounce from "lodash/debounce"
import {Org} from "../../resources/org"

const PAGE_SIZE = 50

type Props = {
	orgs: Array<Org>
	orgId?: string
	setOrgId: (orgId: string) => void
	loadOrgsPage: (offset: number, limit: number, query?: string) => Promise<Array<Org>>
	isDisabled?: boolean
}

// A searchable Org select that loads orgs in small batches (50 at a time) instead of requiring the
// full org list up front. The first batch loads on mount; typing searches from the backend (debounced,
// resets to the first page); scrolling to the bottom of the menu loads the next batch of the current
// query. The *selected* org is resolved from whatever's already loaded (this component's own `options`,
// falling back to the parent-provided `orgs`) so picking a result never blanks out waiting on a
// redundant round trip, and survives later search resets that replace `options`.
export function OrgSelect({orgs, orgId, setOrgId, loadOrgsPage, isDisabled}: Props) {
	const [options, setOptions] = useState<Array<Org>>([])
	const [offset, setOffset] = useState(0)
	const [hasMore, setHasMore] = useState(true)
	const [isLoading, setIsLoading] = useState(false)
	const [isLoadingMore, setIsLoadingMore] = useState(false)
	const [hasError, setHasError] = useState(false)
	const [query, setQuery] = useState("")
	// Caches the full Org object for whatever was most recently picked *in this component*, so the
	// selected value keeps displaying correctly even after a search reset replaces `options` with a
	// different page that no longer contains it (see `selectedOption` below).
	const [selectedOrg, setSelectedOrg] = useState<Org | undefined>()
	const loadedInitialRef = useRef(false)

	// Request bookkeeping so a newer request (e.g. a fresh search while a scroll-triggered page fetch is
	// still in flight, or a reset while a search is in flight) always wins instead of being dropped:
	// - `requestKeyRef` dedupes only *exact* repeat calls (e.g. react-select firing
	//   `onMenuScrollToBottom` more than once for the same page before state updates commit).
	// - `requestIdRef` lets an older, now-superseded request's response be ignored when it resolves,
	//   instead of clobbering state set by a newer request that completed first.
	const requestIdRef = useRef(0)
	const pendingRequestKeyRef = useRef<string | null>(null)
	// Remembers the params of whatever request is currently in flight (or most recently was), so `retry`
	// can re-issue the *exact* same request on failure instead of recomputing it from `offset`/`query`
	// state - which, for a replacement request, is reset to 0 synchronously below but may not have
	// re-rendered yet, and would otherwise cause Retry to resume mid-list instead of from the top.
	const lastRequestRef = useRef({offset: 0, query: "", replace: true})

	const loadPage = useCallback(
		async (nextOffset: number, searchQuery: string, replace: boolean) => {
			const requestKey = `${replace ? "replace" : "append"}:${searchQuery}:${nextOffset}`
			if (pendingRequestKeyRef.current === requestKey) return
			const requestId = ++requestIdRef.current
			pendingRequestKeyRef.current = requestKey
			lastRequestRef.current = {offset: nextOffset, query: searchQuery, replace}
			if (replace) {
				setIsLoading(true)
				// Reset immediately (not just on success) so a scroll-to-bottom triggered before this
				// resolves - or a Retry after this fails - can't append to/resume from the previous
				// query's offset.
				setOffset(0)
			} else {
				setIsLoadingMore(true)
			}
			setHasError(false)
			try {
				const page = await loadOrgsPage(nextOffset, PAGE_SIZE, searchQuery)
				if (requestIdRef.current !== requestId) return // superseded by a newer request; ignore this response
				setOptions((prev) => (replace ? page : [...prev, ...page]))
				setOffset(nextOffset + page.length)
				setHasMore(page.length === PAGE_SIZE)
			} catch {
				if (requestIdRef.current === requestId) setHasError(true)
			} finally {
				if (requestIdRef.current === requestId) {
					pendingRequestKeyRef.current = null
					setIsLoading(false)
					setIsLoadingMore(false)
				}
			}
		},
		[loadOrgsPage]
	)

	useEffect(() => {
		if (loadedInitialRef.current) return
		loadedInitialRef.current = true
		loadPage(0, "", true)
	}, [loadPage])

	const debouncedSearch = useMemo(
		() =>
			debounce((value: string) => {
				loadPage(0, value, true)
			}, 300),
		[loadPage]
	)
	useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

	// Once the user is done with a search (picks a result, blurs, or closes the menu), go back to the
	// default, unfiltered list. Otherwise `query` (and the loaded `options`) would stay stuck on the
	// last typed search term, so reopening the menu - or scrolling through it - would keep sending that
	// stale `filter[query]` even though nothing is visibly typed in the box anymore.
	const handleInputChange = (value: string, meta: InputActionMeta) => {
		if (meta.action === "input-change") {
			setQuery(value)
			debouncedSearch(value)
			return
		}
		// meta.action is "set-value" (an option was picked), "input-blur", or "menu-close".
		if (query !== "") {
			debouncedSearch.cancel()
			setQuery("")
			loadPage(0, "", true)
		}
	}

	// Scroll-triggered pagination must stay blocked for as long as *any* request is in flight or the
	// last one failed:
	// - `isLoading`/`isLoadingMore`: a pending replacement search hasn't reset `offset`/`options` in
	//   state yet (only `lastRequestRef` knows that's happening), so an append fired here would still
	//   read the *previous* query's offset and, once the replacement response comes back, mix the old
	//   append results into the new, unrelated page.
	// - `hasError`: a failed replacement/reset leaves the *previous* query's options and `hasMore` in
	//   place (so the user still sees something instead of a blank list), but appending onto them here
	//   would both mix stale results with whatever the retry eventually loads, and overwrite
	//   `lastRequestRef` so Retry can no longer complete the failed request.
	// A fresh search can still supersede any of this - it's triggered independently via
	// `debouncedSearch`/`handleInputChange`, not gated by this guard.
	const canPaginate = hasMore && !isLoading && !isLoadingMore && !hasError

	const handleMenuScrollToBottom = () => {
		if (!canPaginate) return
		loadPage(offset, query, false)
	}

	const retry = useCallback(() => {
		const {offset, query, replace} = lastRequestRef.current
		loadPage(offset, query, replace)
	}, [loadPage])

	const handleChange = (option: Org | null) => {
		if (!option) return
		setOrgId(option.id)
		setSelectedOrg(option)
	}

	// Resolve the selected option from whatever's already available locally first - this component's own
	// loaded `options`, or its cached last selection - before falling back to the parent-provided `orgs`
	// (used for the initial default / duplicated-product org). This avoids blanking out the field or
	// waiting on a redundant fetch when the chosen org was already loaded here.
	const selectedOption =
		(selectedOrg?.id === orgId ? selectedOrg : undefined) ??
		options.find((org) => org.id === orgId) ??
		orgs.find((org) => org.id === orgId) ??
		null

	// Keep the latest loading/error state readable without making the custom components' identity
	// depend on them: if a component passed via `components` were recreated whenever these change (e.g.
	// via a `useCallback` dep), react-select would treat it as a *new* component type on every toggle and
	// remount the whole menu - which both drops the footer row before it's visible and resets menu scroll
	// position back to the top mid-scroll. Refs let the (stable) components read current values on each
	// render without ever changing identity themselves.
	const isLoadingMoreRef = useRef(isLoadingMore)
	isLoadingMoreRef.current = isLoadingMore
	const hasErrorRef = useRef(hasError)
	hasErrorRef.current = hasError
	const retryRef = useRef(retry)
	retryRef.current = retry

	const retryButton = (
		<button
			type="button"
			onClick={() => retryRef.current()}
			style={{
				border: "none",
				background: "none",
				padding: 0,
				color: "#3273dc",
				textDecoration: "underline",
				cursor: "pointer",
				font: "inherit",
			}}
		>
			Retry
		</button>
	)

	// Appends a "Loading more…"/error+retry row below the options, so scrolling to the bottom of the
	// menu gives visible feedback instead of silently doing nothing (while loading), or silently giving
	// up with no way to recover (on failure).
	const MenuList = useCallback(
		(menuListProps: MenuListProps<Org, false>) => (
			<components.MenuList {...menuListProps}>
				{menuListProps.children}
				{isLoadingMoreRef.current && (
					<div style={{textAlign: "center", padding: "8px", fontSize: 13, color: "#999"}}>Loading more…</div>
				)}
				{hasErrorRef.current && !isLoadingMoreRef.current && (
					<div style={{textAlign: "center", padding: "8px", fontSize: 13, color: "#999"}}>
						Couldn&apos;t load orgs. {retryButton}
					</div>
				)}
			</components.MenuList>
		),
		[]
	)

	// Shown instead of `MenuList` when there are no options at all - e.g. the very first page failed.
	const NoOptionsMessage = useCallback(
		(noticeProps: NoticeProps<Org, false>) =>
			hasErrorRef.current ? (
				<components.NoOptionsMessage {...noticeProps}>
					Couldn&apos;t load orgs. {retryButton}
				</components.NoOptionsMessage>
			) : (
				<components.NoOptionsMessage {...noticeProps} />
			),
		[]
	)

	return (
		<Select<Org, false>
			options={options}
			getOptionLabel={(org) => org.attributes.name}
			getOptionValue={(org) => org.id}
			value={selectedOption}
			onChange={handleChange}
			onInputChange={handleInputChange}
			onMenuScrollToBottom={handleMenuScrollToBottom}
			isLoading={isLoading}
			isDisabled={isDisabled}
			components={{MenuList, NoOptionsMessage}}
			placeholder="Select org…"
			classNamePrefix="org-select"
			menuPortalTarget={document.body}
			menuPosition="fixed"
			styles={{
				container: (base) => ({...base, width: "100%"}),
				// Other native <select> elements on this form draw their own dropdown-arrow icon above
				// our menu unless we lift it above everything via a body-level portal with a high z-index.
				menuPortal: (base) => ({...base, zIndex: 9999}),
			}}
		/>
	)
}
